import React, { useState } from 'react';
import styled from 'styled-components';
import {
  User,
  Bell,
  Settings,
  ChevronDown,
  LogOut,
  Menu,
  X,
} from 'lucide-react';

const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  position: sticky;
  top: 0;
  z-index: 100;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const LogoIcon = styled.img`
  width: 152px;
  height: 60px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
  }
`;

const Avatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'src',
})<{ src?: string }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ src, theme }) =>
    src
      ? `url(${src}) center/cover`
      : `linear-gradient(135deg, ${theme.colors.chart.blue}, ${theme.colors.chart.purple})`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const UserName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const UserRole = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Badge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 2px solid ${({ theme }) => theme.colors.surface};
`;

const NotificationButton = styled.div`
  position: relative;
`;

const UserDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 180px;
  z-index: 200;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transform: ${({ show }) => (show ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease-in-out;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceLight};
  }

  &:first-child {
    border-radius: ${({ theme }) => theme.borderRadius.md}
      ${({ theme }) => theme.borderRadius.md} 0 0;
  }

  &:last-child {
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.md}
      ${({ theme }) => theme.borderRadius.md};
  }
`;

const UserContainer = styled.div`
  position: relative;
`;

const NotificationDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 320px;
  max-width: 400px;
  z-index: 200;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transform: ${({ show }) => (show ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease-in-out;
  margin-top: ${({ theme }) => theme.spacing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    top: 60px; /* Altura do header */
    right: 8px;
    left: 8px;
    min-width: unset;
    max-width: unset;
    width: auto;
    margin-top: 0;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    right: 4px;
    left: 4px;
    top: 56px;
  }
`;

const NotificationHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px 16px;
  }
`;

const NotificationList = styled.div`
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-height: calc(
      100vh - 160px
    ); /* Ajusta para altura da tela - header - footer */
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-height: calc(100vh - 140px);
  }
`;

const NotificationItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'type',
})<{ type?: string }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px 16px;
    gap: 12px;
  }

  background: ${({ type, theme }) => {
    switch (type) {
      case 'urgent':
        return `${theme.colors.error}08`;
      case 'success':
        return `${theme.colors.success}08`;
      case 'info':
        return `${theme.colors.info}08`;
      default:
        return 'transparent';
    }
  }};

  &:hover {
    background: ${({ type, theme }) => {
      switch (type) {
        case 'urgent':
          return `${theme.colors.error}15`;
        case 'success':
          return `${theme.colors.success}15`;
        case 'info':
          return `${theme.colors.info}15`;
        default:
          return theme.colors.surfaceLight;
      }
    }};
    transform: translateX(2px);
  }

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${({ type, theme }) => {
      switch (type) {
        case 'urgent':
          return theme.colors.error;
        case 'success':
          return theme.colors.success;
        case 'info':
          return theme.colors.info;
        default:
          return theme.colors.border.light;
      }
    }};
  }
`;

const NotificationIcon = styled.div`
  font-size: 20px;
  line-height: 1;
  margin-top: 2px;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 18px;
    margin-top: 1px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 16px;
    margin-top: 0;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  line-height: 1.4;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin-bottom: 4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    line-height: 1.3;
  }
`;

const NotificationMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  line-height: 1.4;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    margin-bottom: 4px;
    line-height: 1.5;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    line-height: 1.4;
  }
`;

const NotificationTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 11px;
  }
`;

const NotificationFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.sm}
      ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 12px 16px;
  }
`;

const NotificationFooterLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: background-color 0.2s ease;

  &:hover {
    text-decoration: underline;
    background-color: ${({ theme }) => `${theme.colors.primary}10`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    padding: 10px 20px;
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    padding: 12px 16px;
  }
`;

const CloseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

interface HeaderProps {
  userName?: string;
  userRole?: string;
  userAvatar?: string;
  onLogout?: () => void;
  onMenuToggle?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  userName = 'Joana',
  userRole = 'Gestor',
  userAvatar,
  onLogout,
  onMenuToggle,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Mock data para notificações
  const notifications = [
    {
      id: 1,
      title: 'Entrevista de desligamento',
      message: 'Há 3 documentos pendentes de assinatura',
      time: '30 min atrás',
      type: 'urgent',
      icon: '⚠️',
    },
    {
      id: 2,
      title: 'Ranking mensal finalizado',
      message: 'Clique aqui para ver os resultados',
      time: '2 horas atrás',
      type: 'success',
      icon: '🏆',
    },
    {
      id: 3,
      title: 'Avaliações de período de experiência',
      message: 'Há 8 avaliações aguardando sua revisão',
      time: '4 horas atrás',
      type: 'info',
      icon: '📋',
    },
  ];

  // Fechar dropdown quando clicar fora
  React.useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
      setShowNotifications(false);
    };

    if (showDropdown || showNotifications) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown, showNotifications]);
  return (
    <HeaderContainer>
      <LeftSection>
        <MobileMenuButton onClick={onMenuToggle}>
          <Menu size={20} />
        </MobileMenuButton>
        <Logo>
          <LogoIcon src="/images/logo.png" alt="HSPW Logo" />
        </Logo>
      </LeftSection>

      <UserSection>
        <NotificationButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setShowNotifications(!showNotifications);
            }}
          >
            <Bell size={16} />
          </IconButton>
          <Badge />

          <NotificationDropdown show={showNotifications}>
            <NotificationHeader>
              <span>Notificações ({notifications.length})</span>
              <CloseButton onClick={() => setShowNotifications(false)}>
                <X size={18} />
              </CloseButton>
            </NotificationHeader>

            <NotificationList>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  type={notification.type}
                >
                  <NotificationIcon>{notification.icon}</NotificationIcon>
                  <NotificationContent>
                    <NotificationTitle>{notification.title}</NotificationTitle>
                    <NotificationMessage>
                      {notification.message}
                    </NotificationMessage>
                    <NotificationTime>{notification.time}</NotificationTime>
                  </NotificationContent>
                </NotificationItem>
              ))}
            </NotificationList>

            <NotificationFooter>
              <NotificationFooterLink
                onClick={() => setShowNotifications(false)}
              >
                Ver todas as notificações
              </NotificationFooterLink>
            </NotificationFooter>
          </NotificationDropdown>
        </NotificationButton>

        <IconButton>
          <Settings size={16} />
        </IconButton>

        <UserContainer>
          <UserProfile
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
          >
            <Avatar src={userAvatar}>
              {!userAvatar && <User size={16} />}
            </Avatar>
            <UserInfo>
              <UserName>{userName}</UserName>
              <UserRole>{userRole}</UserRole>
            </UserInfo>
            <ChevronDown size={12} />
          </UserProfile>

          <UserDropdown show={showDropdown}>
            <DropdownItem onClick={() => setShowDropdown(false)}>
              <Settings size={16} />
              Configurações
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                setShowDropdown(false);
                onLogout?.();
              }}
            >
              <LogOut size={16} />
              Sair
            </DropdownItem>
          </UserDropdown>
        </UserContainer>
      </UserSection>
    </HeaderContainer>
  );
};
